/* Core & libraries */
import React, {useEffect, useState, useRef} from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import { Row, Col } from "react-bootstrap";
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import {
  useValidationService,
  createValidationPolicyYupSchema
} from "../../../../resources/validations/validations";
import { useReportsService } from "../../../../services/reportsService";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { PAGE_TITLES } from "../../../../constants/appConstants";
import { useLocaleContext } from "../../../../contexts/localeContext";

/* Components */
import UCSelect from "../../../ucselect";
import UCButton from "../../../buttons";
import { ReactComponent as DownloadIcon } from "../../../../resources/images/download-02.svg";

/* Styles */
import styles from "./styles.js"
import { colors } from "../../../../constants/colors";


const getCurrentYear = () => {
  const date = new Date();
  const currentYear =  date.getFullYear();
  return currentYear;
}

const startingYear = 2023;


const monthsText = defineMessages({
  jan: {
    id: "general.months.january",
  },
  feb: {
    id: "general.months.february",
  },
  mar: {
    id: "general.months.march",
  },
  apr: {
    id: "general.months.april",
  },
  may: {
    id: "general.months.may",
  },
  jun: {
    id: "general.months.june",
  },
  jul: {
    id: "general.months.july",
  },
  aug: {
    id: "general.months.august",
  },
  sep: {
    id: "general.months.september",
  },
  oct: {
    id: "general.months.october",
  },
  nov: {
    id: "general.months.november",
  },
  dev: {
    id: "general.months.december",
  },
})

const accMessages = defineMessages({
  monthAriaLabel: {
    id: "receipts.monthAriaLabel"
  },
  yearAriaLabel: {
    id: "receipts.yearAriaLabel"
  },
  downloadReceiptsCSV: {
    id: "receipts.button"
  }
})

const ReceiptsTab = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();

  const formRef = useRef(null);
  const locale = useLocaleContext();

  const { downloadReceiptsMockedValidationObj } = useValidationService()
  const [downloadSchema, setDownloadSchema] = useState({});

  const { downloadReceipts } = useReportsService();
  const { initAnalytics } = useAnalytics();

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["receipts"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
  }, []);

  useEffect(() => {
    (async () => {
      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(downloadReceiptsMockedValidationObj);
      setDownloadSchema(result)
    })();
  }, []);

  useEffect(() => {
    //changed body style for dropdowns visibility on the page
    document.body.style.overflow = "visible";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values) => {    
    const options = getMonths()
    let monthId
    options.map((monthObject) => {
      if(monthObject.monthName === values.month) {
        monthId = monthObject.monthId;
      }  
    })
    downloadReceipts({
      month: monthId,
      year: values?.year,
    })
  }

  const getMonths = () => {
    let months = [];
    let index = 1;
    Object.keys(monthsText).forEach((month) => {
      let object = {monthId: index, monthName: intl.formatMessage(monthsText[month]) }
        months.push(object);
        index = index + 1
    })

    return months;
  }

  const getYears = () => {
    let currentYear =  getCurrentYear();
    let years = [];
    let yearToAdd = currentYear;
    for (let i = 0; i <= (currentYear-startingYear); i++) {
      let object = { year: yearToAdd }
      years.push(object);
      yearToAdd = yearToAdd - 1;
    }
    return years;
  }
  
  return (
    <div className={classes.receiptsTab}>
      <div>
        <div className={classes.topContainer} tabIndex={"0"}>
          <h1 className={classes.title}><FormattedMessage id="receipts.tabTitle"/></h1>
          <h2 className={classes.description}><FormattedMessage id="receipts.tabDescription"/></h2>
        </div>
        <div className={classes.bottomContainer}>
          <Formik
            innerRef={formRef}
            initialValues={{
              month: getMonths()[moment().month()].monthName,
              year: getCurrentYear()
            }}
            validationSchema={downloadSchema}
            onSubmit={(values, { setSubmitting }) => {
              submitForm(values, setSubmitting);
            }}
          >
            {({
                values,
                errors,
                touched,
                setFieldTouched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue
              }) => {
              return (
                <>
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-12 col-md-12">
                        <div className={classes.formContainer}>
                          <Col>
                            <Row>
                              <div className={classes.inputMonthContainer}>
                                <label htmlFor="month" className={classes.label}>
                                  <FormattedMessage id="receipts.month"/>
                                </label>
                                {
                                  <Field
                                    type="select"
                                    name="month"
                                    id="month"
                                    render={({ form }) => (
                                      <div className={classes.rolesWrapper}>
                                        <UCSelect
                                          className={classes.roles}
                                          options={getMonths()}
                                          defaultValue={getMonths()[moment().month()]}
                                          key={locale}
                                          onChange={(item) => form.setFieldValue("month", item.monthName)}
                                          ariaLabel={intl.formatMessage(accMessages.monthAriaLabel)}
                                          getOptionLabel={(option) => option.monthName}
                                          getOptionValue={(option) => option.monthName}
                                        />
                                        <ErrorMessage
                                          name="month"
                                          component="span"
                                          className={classes.error}
                                        />
                                      </div>
                                    )}
                                  />
                                }
                              </div>
                            </Row>
                          </Col>
                          <Col >
                            <Row>
                              <div className={classes.inputYearContainer}>
                                <label htmlFor="year" className={classes.label}>
                                  <FormattedMessage id="receipts.year"/>
                                </label>
                                {
                                  <Field
                                    type="select"
                                    name="year"
                                    id="year"
                                    render={({ form }) => (
                                      <div className={classes.rolesWrapper}>
                                        <UCSelect
                                          className={classes.roles}
                                          options={getYears()}
                                          defaultValue={getYears().find(item => item.year === values.year)}
                                          onChange={(item) => form.setFieldValue("year", item.year)}
                                          ariaLabel={intl.formatMessage(accMessages.yearAriaLabel)}
                                          getOptionLabel={(option) => option.year}
                                          getOptionValue={(option) => option.year}
                                        />
                                        <ErrorMessage
                                          name="year"
                                          component="span"
                                          className={classes.error}
                                        />
                                      </div>
                                    )}
                                  />
                                }
                              </div>
                            </Row>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </form>
                </>
              )
            }
            }
          </Formik>
          {/* <a onClick={handleFormSubmit} download="MyExampleDoc" target='_blank' className={classes.button}>
            <span className={classes.icon}><DownloadIcon /></span><FormattedMessage id="receipts.button"/>
          </a> */}
          <UCButton
            type="primary"
            className={classes.button}
            onClick={handleFormSubmit}
            text={<FormattedMessage id="receipts.button"/>}
            icon={<DownloadIcon />}
            ariaLabel={intl.formatMessage(accMessages.downloadReceiptsCSV)}
            loadingStateStyling={classes.downloadCsvLoadingState}
            tabIndex={0}
            spinner={{
              name: "downloadReceiptsSpinner",
              size: 20,
            }}
          />       
        </div>
      </div>
    </div>
  )
}

export default ReceiptsTab;
